import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

// Admin: list topics
export const listTopics = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles").select("role").eq("user_id", userId);
    const isAdmin = roles?.some((r: any) => r.role === "admin");
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("blog_topics").select("*").order("audience").order("rotation_order");
    if (error) throw error;
    return { topics: data };
  });

// Admin: list recent posts
export const listPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles").select("role").eq("user_id", userId);
    const isAdmin = roles?.some((r: any) => r.role === "admin");
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("blog_posts")
      .select("id, slug, title, audience, city_slug, published_at, status")
      .order("published_at", { ascending: false })
      .limit(50);
    if (error) throw error;
    return { posts: data };
  });

// Admin: get rotation state + recent logs
export const getBlogStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles").select("role").eq("user_id", userId);
    const isAdmin = roles?.some((r: any) => r.role === "admin");
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: state }, { data: logs }, { count: postCount }] = await Promise.all([
      supabaseAdmin.from("blog_rotation_state").select("*").eq("id", 1).single(),
      supabaseAdmin.from("blog_generation_log").select("*").order("created_at", { ascending: false }).limit(20),
      supabaseAdmin.from("blog_posts").select("*", { count: "exact", head: true }),
    ]);
    return { state, logs, postCount };
  });

// Admin: manual trigger
export const generateNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ slot: z.enum(["general", "city"]) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roles } = await supabase
      .from("user_roles").select("role").eq("user_id", userId);
    const isAdmin = roles?.some((r: any) => r.role === "admin");
    if (!isAdmin) throw new Error("Forbidden");

    const { runScheduledGeneration } = await import("@/lib/blog.server");
    const results = await runScheduledGeneration(data.slot);
    return { ok: true, results };
  });

// Public: list published posts (for hub/listing pages)
export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ citySlug: z.string().optional(), limit: z.number().min(1).max(50).default(20) }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("blog_posts")
      .select("id, slug, title, meta_description, audience, city_slug, published_at, tldr")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(data.limit);
    if (data.citySlug) q = q.eq("city_slug", data.citySlug);
    const { data: posts, error } = await q;
    if (error) throw error;
    return { posts: posts ?? [] };
  });

// Public: get one post
export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z.object({ citySlug: z.string(), slug: z.string() }).parse(input),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: post, error } = await supabaseAdmin
      .from("blog_posts")
      .select("*")
      .eq("city_slug", data.citySlug)
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw error;
    return { post };
  });
